import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { map, pluck, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/common/http';
var ApiVersion;
(function (ApiVersion) {
    ApiVersion["v1"] = "v1";
    ApiVersion["v2"] = "v2";
    ApiVersion["v3"] = "v3";
    ApiVersion["v4"] = "v4";
    ApiVersion["v5"] = "v5";
    ApiVersion["v6"] = "v6";
    ApiVersion["v7"] = "v7";
    ApiVersion["v8"] = "v8";
})(ApiVersion || (ApiVersion = {}));
class UrlBase {
    /**
     *
     * @param version - v1, v2, v3, v4, v5, v6, v7
     */
    static getBaseUrl(version) {
        return `/api/accounting/${version}/`;
    }
    // this is mainly used for tickmarks
    static getNotesBaseUrl(version) {
        return `/api/notes/${version}/`;
    }
    // this is mainly used for contacts
    static getCoreBaseUrl(version) {
        return `/api/core/${version}/`;
    }
    // this is mainly use for the preferences
    static getPreferencesUrl(version) {
        return `/api/profiles/${version}/preferences`;
    }
    static getAccountMappingUrl(version) {
        return `/api/account-mapping/${version}/`;
    }
}

class AccountService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Given an account, create a display string
     * @param account - Account
     * @param displayStringSeparator - separator to use when formatting the display string
     */
    formatDisplayString(account, displayStringSeparator = '|') {
        let displayString;
        if (account.accountNumber && account.description) {
            displayString = `${account.accountNumber} ${displayStringSeparator} ${account.description}`;
        }
        else if (account.accountNumber) {
            displayString = account.accountNumber;
        }
        else if (account.description) {
            displayString = account.description;
        }
        return displayString;
    }
    getAll(ownerId, 
    // Because of how this function is called, we want to keep the ordering here and suppress the linter
    // eslint-disable-next-line default-param-last
    includeBalances = false, 
    // eslint-disable-next-line default-param-last
    displayStringSeparator = '|', fiscalYearId) {
        let prepend = '?';
        let url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/account/all`;
        // if they include balances, add it to the query string
        if (includeBalances) {
            url += prepend + 'includeBalances=true';
            prepend = '&';
        }
        // if they provided a fiscalYearId, add it to the query string
        if (fiscalYearId !== undefined) {
            url += prepend + `fiscalYearId=${fiscalYearId}`;
            prepend = '&';
        }
        return this.http.get(url).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => {
            return response.data.map(account => {
                // Formats the display string for comboboxes
                const displayString = this.formatDisplayString(account, displayStringSeparator);
                return Object.assign(Object.assign({}, account), { id: account.accountId, displayString });
            });
        }));
    }
    getByAccountGroup(ownerId, accountGroupId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/account/accountGroup/${accountGroupId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    getByTaxSchedule(ownerId, taxScheduleId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/account/taxSchedule/${taxScheduleId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    // Returns the accounts for given financial report types
    getByFinancialReportType(ownerId, financialReportTypes) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/account/getby/financialReportTypes?`;
        financialReportTypes.forEach(type => {
            url += `financialReportTypes=${type}&`;
        });
        // Removes the trailing ampresand
        url = url.substring(0, url.length - 1);
        return this.http.get(url).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => {
            return response.data.map(account => {
                // Formats the display string for comboboxes
                const displayString = this.formatDisplayString(account, '|');
                return Object.assign(Object.assign({}, account), { id: account.accountId, displayString });
            });
        }));
    }
    addAccount(ownerId, accountsToAdd) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/account`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, accountsToAdd).pipe(map((response) => response.data));
    }
    updateAccount(ownerId, accountToUpdate, fiscalYearId) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/account/${accountToUpdate.account.id}`;
        // check if they provided a fiscalYearId, if so add to url.
        if (fiscalYearId !== undefined) {
            url += `?fiscalYearId=${fiscalYearId}`;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, accountToUpdate).pipe(map((response) => response.data));
    }
    /**
     * Deletes accounts
     * @param ownerId - delete the accounts for the ownerid
     * @param statusDto - DTO that has the list of ids and the checkitems
     * @returns List of ids for the deleted accounts
     */
    deleteAccounts(ownerId, statusDto) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/account/delete`;
        return (this.http
            .post(url, statusDto)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    /**
     * Get totals for accounts
     * @param ownerId - The owner ID to get the totals for
     */
    getTotals(ownerId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/account/totals`;
        return (this.http
            .get(url)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    /**
     * Assign a list of unassigned category node accounts
     * @param ownerId - Assign the accounts for the owner Id
     * @param accounts - accounts list to assign
     * @param gridId - grid identificator
     * @returns List of assigned category node accounts
     */
    assignCategoryNodeAccounts(ownerId, accounts, gridId) {
        const categoryNodeAccountDto = {
            categoryNodeAccounts: accounts,
        };
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/categorynode/account/move?trialBalanceGridId=${gridId}`;
        return (this.http
            .put(url, categoryNodeAccountDto)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    /**
     * Assign a list of unassigned tax code accounts
     * @param ownerId - Assign the accounts for the owner Id
     * @param accounts - accounts list to assign
     * @param gridId - grid identificator
     * @returns List of assigned tax code accounts
     */
    assignTaxCodeAccounts(ownerId, accounts, gridId) {
        const taxCodesAccountDto = {
            taxCodeAccounts: accounts,
        };
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/taxCode/account/move?trialBalanceGridId=${gridId}`;
        return (this.http
            .put(url, taxCodesAccountDto)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
}
AccountService.ɵfac = function AccountService_Factory(t) { return new (t || AccountService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountService_Factory() { return new AccountService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountService, providedIn: "root" });
AccountService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountBalanceTypeService {
    constructor(http) {
        this.http = http;
    }
    get(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/accountBalanceType`;
        return (this.http
            .get(url)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
}
AccountBalanceTypeService.ɵfac = function AccountBalanceTypeService_Factory(t) { return new (t || AccountBalanceTypeService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountBalanceTypeService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountBalanceTypeService_Factory() { return new AccountBalanceTypeService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountBalanceTypeService, providedIn: "root" });
AccountBalanceTypeService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountBalanceTypeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class FiscalYearService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Requests list of fiscal years
     * @param ownerId - Fiscal years owner id
     * @param includePeriods - Include periods into the fiscal year list flag
     */
    get(ownerId, includePeriods = false) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/fiscalYear?includePeriods=${includePeriods.toString()}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     * Requests a single fiscal year for a specific owner ID.
     * @param ownerId - The Id of the owner to get the fiscal year for.
     * @param periodTypeOfCurrent - The period type used to determine which is the current fiscal year.
     * @returns The current fiscal year.
     */
    getCurrentFiscalYear(ownerId, periodTypeOfCurrent) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/fiscalYear/periodTypeOfCurrent/${periodTypeOfCurrent}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     * Adds new fiscal year
     * @param ownerId - Owner id for added fiscal year
     * @param fiscalYear - New fiscal year
     */
    add(ownerId, fiscalYear) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/fiscalYear`;
        return this.http.post(url, fiscalYear).pipe(pluck('data'));
    }
    /**
     * Edit fiscal year
     * @param ownerId - Owner id for added fiscal year
     * @param fiscalYear - Edited fiscal year
     * @param yearEndCloseSetting - Year End Close Settings
     */
    update(ownerId, fiscalYear, yearEndCloseSetting) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/fiscalYear/${fiscalYear.id}`;
        return this.http.put(url, { fiscalYear, yearEndCloseSetting }).pipe(pluck('data'));
    }
    /**
     * Updates and maps fiscal year periods
     * @param ownerId - Owner id for added fiscal year
     * @param fiscalYear - Edited fiscal year with periods
     */
    updatePeriods(ownerId, fiscalYear) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/fiscalYear/${fiscalYear.id}/periods`;
        return this.http.put(url, fiscalYear).pipe(pluck('data'));
    }
}
FiscalYearService.ɵfac = function FiscalYearService_Factory(t) { return new (t || FiscalYearService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
FiscalYearService.ɵprov = i0.ɵɵdefineInjectable({ factory: function FiscalYearService_Factory() { return new FiscalYearService(i0.ɵɵinject(i1.HttpClient)); }, token: FiscalYearService, providedIn: "root" });
FiscalYearService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FiscalYearService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class PeriodService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Returns all periods for passed owner id and period type
     * @param ownerId - owner id
     * @param periodType - period type(optional)
     * @returns Observable Period[]
     */
    get(ownerId, periodType) {
        let url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/period`;
        // check if they provided a period type, if so tack it on
        if (periodType !== undefined) {
            url += `?periodType=${periodType}`;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     * Returns current period
     * @param ownerId - owner id
     * @returns Observable Period
     */
    getCurrentPeriod(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/currentPeriod`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
PeriodService.ɵfac = function PeriodService_Factory(t) { return new (t || PeriodService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
PeriodService.ɵprov = i0.ɵɵdefineInjectable({ factory: function PeriodService_Factory() { return new PeriodService(i0.ɵɵinject(i1.HttpClient)); }, token: PeriodService, providedIn: "root" });
PeriodService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(PeriodService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TickmarkService {
    // constructor - http
    constructor(http) {
        this.http = http;
    }
    // get for http client
    getHttpClient() {
        return this.http;
    }
    // get for tickmarks endpoint
    get(product, country) {
        const url = UrlBase.getNotesBaseUrl(ApiVersion.v1) + `tickmarks/product/${product}/country/${country}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    // put for tickmarks endpoint
    put(tickmark) {
        if (tickmark !== undefined) {
            if (tickmark.id === null) {
                throw new Error('tickmark Id error');
            }
            else if (tickmark.imageId === null) {
                throw new Error('tickmark ImageId error');
            }
            else if (tickmark.description === null) {
                throw new Error('tickmark description error');
            }
            else if (tickmark.country === null) {
                throw new Error('tickmark Country error');
            }
            else if (tickmark.product === null) {
                throw new Error('tickmark Product error');
            }
            else if (tickmark.created === null) {
                throw new Error('tickmark Created error');
            }
            else if (tickmark.changed === null) {
                throw new Error('tickmark Changed error');
            }
            else if (tickmark.isdeleted === null) {
                throw new Error('tickmark IsDeleted error');
            }
            else if (tickmark.createdbyid === null) {
                throw new Error('tickmark CreatedByContactId error');
            }
            else {
                const url = UrlBase.getNotesBaseUrl(ApiVersion.v1) + `tickmarks/${tickmark.id}`;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return this.http.put(url, tickmark).pipe(map((response) => response.data));
            }
        }
    }
}
TickmarkService.ɵfac = function TickmarkService_Factory(t) { return new (t || TickmarkService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TickmarkService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TickmarkService_Factory() { return new TickmarkService(i0.ɵɵinject(i1.HttpClient)); }, token: TickmarkService, providedIn: "root" });
TickmarkService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TickmarkService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TaxScheduleService {
    constructor(http) {
        this.http = http;
    }
    get(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/taxSchedule`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, {}).pipe(map((response) => response.data));
    }
    getByFiscalYear(ownerId, fiscalYearId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/taxSchedule/fiscalYear/${fiscalYearId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
    /**
     * Get a tax export option for third party vendors. (CCH, Lacerte, UltraTax)
     * @param ownerId - Owner ID for the requested client/ contact that wants their tax codes exported
     */
    getTaxScheduleExportOption(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/taxSchedule/export/option`;
        return this.http.get(url);
    }
    /**
     * Exports the CCH file
     * @param ownerId - Owner ID for the requested client/ contact that wants their tax codes exported
     * @param taxYear - The tax year to get the tax data for
     * @param exportOptionsV1Dto - The export options DTO the user specifies
     */
    exportCchFile(ownerId, taxYear, exportOptionV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/taxSchedule/taxYear/${taxYear}/export?vendorType=cch`;
        return this.http.post(url, exportOptionV1Dto, { responseType: 'text' });
    }
    /**
     * Exports the Lacerte file
     * @param ownerId - Owner ID for the requested client/ contact that wants their tax codes exported
     * @param taxYear - The tax year to get the tax data for
     * @param exportOptionsV1Dto - The export options DTO the user specifies
     */
    exportLacerteFile(ownerId, taxYear, exportOptionV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/taxSchedule/taxYear/${taxYear}/export?vendorType=lacerte`;
        return this.http.post(url, exportOptionV1Dto, { responseType: 'text' });
    }
}
TaxScheduleService.ɵfac = function TaxScheduleService_Factory(t) { return new (t || TaxScheduleService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TaxScheduleService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TaxScheduleService_Factory() { return new TaxScheduleService(i0.ɵɵinject(i1.HttpClient)); }, token: TaxScheduleService, providedIn: "root" });
TaxScheduleService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TaxScheduleService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TrialBalanceService {
    constructor(http) {
        this.http = http;
    }
    /**
     * GETS all of the Trial Balance grids for a given owner
     * @param ownerId - owner for whom to get the grids
     * @returns An observable with a TrialBalanceGrid array
     */
    getTrialBalanceGrids(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/trialBalance`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    addAccount(ownerId, accountToAdd) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/trialBalance/addAccount`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, accountToAdd).pipe(map((response) => response.data));
    }
    updateAccount(ownerId, accountToUpdate, fiscalYearId) {
        let url = UrlBase.getBaseUrl(ApiVersion.v3);
        url += `${ownerId}/trialBalance/account/${accountToUpdate.account.id}/updateAccount`;
        // check if they provided a fiscalYearId, if so add to url.
        if (fiscalYearId !== undefined) {
            url += `?fiscalYearId=${fiscalYearId}`;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, accountToUpdate).pipe(map((response) => response.data));
    }
    /**
     * Adds a new trial balance grid view
     * @param ownerId - Owner id
     * @param gridToAdd - Trial Balance Grid to add
     * @returns Added grid
     */
    addGrid(ownerId, gridToAdd) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/trialBalance`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, gridToAdd).pipe(map((response) => response.data));
    }
    /**
     * Updates an existing trial balance grid view
     * @param ownerId - Owner id
     * @param gridToUpdate - Trial Balance grid to update
     * @returns Updated grid
     */
    updateGrid(ownerId, gridToUpdate) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/trialBalance/${gridToUpdate.Id}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, gridToUpdate).pipe(map((response) => response.data));
    }
    /**
     * Deletes an existing trial balance grid view
     * @param ownerId - Onwer id
     * @param gridToDeleteId - Id of Trial Balance grid to delete
     * @returns Empty response
     */
    deleteGrid(ownerId, gridToDeleteId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/trialBalance/${gridToDeleteId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.delete(url);
    }
    /**
     * getTrialBalanceDataPage to load the grid
     * Gets trial balance data page
     * @param trialBalanceGridDataParams - object that passed in
     * @returns trial balance data page
     */
    getTrialBalanceGridPagedData(trialBalanceGridDataParams) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) +
            `${trialBalanceGridDataParams.ownerId}/trialBalance/${trialBalanceGridDataParams.trialBalanceGridId}` +
            `/data?pageNumber=${trialBalanceGridDataParams.pageNumber}` +
            `&pageSize=${trialBalanceGridDataParams.pageSize}&currentPeriodId=${trialBalanceGridDataParams.currentPeriodId}` +
            `&includeDynamicColumns=${trialBalanceGridDataParams.includeDynamicColumns.toString()}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     * Endpoint call to update a specific TrialBalanceColumnValue
     * This can be used for updating balance column values, Workpaper References through the Trial Balance and Tickmarks
     * @param ownerId - owner for whom the value will be updated
     * @param trialBalanceRowId - the rowId to update - corresponds to the accountId for the row
     * @param trialBalanceColumnValueToUpdate - DTO for the column value to update
     * @param currentPeriodId - id of the current period for the owner
     * @returns Rows of TrialBalanceGridData
     */
    updateTrialBalanceColumnValue(ownerId, trialBalanceRowId, trialBalanceColumnValueToUpdate, currentPeriodId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) +
            `${ownerId}/trialBalance/row/${trialBalanceRowId}?currentPeriodId=${currentPeriodId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, trialBalanceColumnValueToUpdate).pipe(map((response) => response.data));
    }
    /**
     * Gets trial balance grid row data
     * @param ownerId - Owner id
     * @param trialBalanceGridId - Trial Balance grid Id
     * @param accountId - Trial alance Account Id
     * @returns trial balance row data
     */
    getTrialBalanceRow(ownerId, trialBalanceGridId, accountId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/trialBalance/${trialBalanceGridId}/row/${accountId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data[0]));
    }
}
TrialBalanceService.ɵfac = function TrialBalanceService_Factory(t) { return new (t || TrialBalanceService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TrialBalanceService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TrialBalanceService_Factory() { return new TrialBalanceService(i0.ɵɵinject(i1.HttpClient)); }, token: TrialBalanceService, providedIn: "root" });
TrialBalanceService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TrialBalanceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class ExternalAccountMappingService {
    constructor(http) {
        this.http = http;
    }
    getAll(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/externalAccountMapping`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    getExternalAccountMapping(ownerId, externalAccountMappingId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/externalAccountMapping/${externalAccountMappingId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    addExternalAccountMapping(ownerId, externalAccountMapping) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/externalAccountMapping`;
        return (this.http
            .post(url, externalAccountMapping)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    updateExternalAccountMapping(ownerId, externalAccountMappingId, externalAccountMapping) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/externalAccountMapping/${externalAccountMappingId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, externalAccountMapping).pipe(map((response) => response.data));
    }
    getQboExternalAccountMapping(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/QboExternalAccountMapping`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
    getExternalAccountMappingV2(ownerId, externalAccountMappingType) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/ExternalAccountMapping/type/${externalAccountMappingType}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
}
ExternalAccountMappingService.ɵfac = function ExternalAccountMappingService_Factory(t) { return new (t || ExternalAccountMappingService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
ExternalAccountMappingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function ExternalAccountMappingService_Factory() { return new ExternalAccountMappingService(i0.ɵɵinject(i1.HttpClient)); }, token: ExternalAccountMappingService, providedIn: "root" });
ExternalAccountMappingService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ExternalAccountMappingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class YearEndCloseService {
    constructor(http) {
        this.http = http;
    }
    getYearEndCloseSettings(ownerId, fiscalYearId) {
        return (this.http
            .get(`${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/yearEndClose/fiscalYear/${fiscalYearId}`)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    closeYear(ownerId, fiscalYearId, settings) {
        return (this.http
            .post(`${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/yearEndClose/fiscalYear/${fiscalYearId}/performYearEndClose`, settings)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
}
YearEndCloseService.ɵfac = function YearEndCloseService_Factory(t) { return new (t || YearEndCloseService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
YearEndCloseService.ɵprov = i0.ɵɵdefineInjectable({ factory: function YearEndCloseService_Factory() { return new YearEndCloseService(i0.ɵɵinject(i1.HttpClient)); }, token: YearEndCloseService, providedIn: "root" });
YearEndCloseService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(YearEndCloseService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountBalanceService {
    constructor(http) {
        this.http = http;
    }
    // Returns the Net Income values for a given balanceTotalParamaters (which specify balance type/period info)
    getNetIncomeTotalsUsingParamaters(ownerId, balanceTotalParamaters, currentPeriodId) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/accountBalances/netIncomeTotalsUsingParameters`;
        if (currentPeriodId !== undefined) {
            url += `?currentPeriodId=${currentPeriodId}`;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, balanceTotalParamaters).pipe(map((response) => response.data));
    }
    /**
     * Returns account balances for the specified balance type
     * @param ownerId - owner Id
     * @param fiscalyearId - fiscal year id
     * @param accountBalanceTypeDescriptions - balance type descriptions
     */
    getAccountBalancesForBalanceType(ownerId, fiscalyearId, accountBalanceTypeDescriptions) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/accountBalances/fiscalYear/${fiscalyearId}?`;
        if ((accountBalanceTypeDescriptions === null || accountBalanceTypeDescriptions === void 0 ? void 0 : accountBalanceTypeDescriptions.length) > 0) {
            accountBalanceTypeDescriptions.forEach(type => {
                url += `accountBalanceTypeDescriptions=${type}&`;
            });
            // Removes the trailing ampersand
            url = url.substring(0, url.length - 1);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     * Get account balances for the specified balance type
     * @param ownerId - owner Id
     * @param periodId - period id
     * @param accountBalanceTypeId - balance type id
     * @returns List of the account balances
     */
    getAccountBalancesFiltered(ownerId, periodId, accountBalanceTypeId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/accountBalances/filtered?periodId=${periodId}&accountBalanceTypeId=${accountBalanceTypeId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
AccountBalanceService.ɵfac = function AccountBalanceService_Factory(t) { return new (t || AccountBalanceService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountBalanceService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountBalanceService_Factory() { return new AccountBalanceService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountBalanceService, providedIn: "root" });
AccountBalanceService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountBalanceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

/* eslint-disable @typescript-eslint/no-explicit-any */
class ProfitAllocationDetailsService {
    constructor(http) {
        this.http = http;
    }
    // add profit allocation details
    addProfitAllocationDetail(ownerId, fiscalYearId, profitAllocationDetail) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/profitAllocationDetails/fiscalYear/${fiscalYearId}`;
        return this.http
            .post(url, profitAllocationDetail)
            .pipe(map((response) => response.data));
    }
    // update profit allocation details
    updateProfitAllocationDetail(ownerId, fiscalYearId, profitAllocationDetail) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/profitAllocationDetails/fiscalYear/${fiscalYearId}`;
        return this.http
            .put(url, profitAllocationDetail)
            .pipe(map((response) => response.data));
    }
    // get profit allocation details
    getProfitAllocationDetail(ownerId, fiscalYearId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/profitAllocationDetails/fiscalYear/${fiscalYearId}`;
        return this.http.get(url).pipe(map((response) => response.data));
    }
    // update profit allocation transaction
    updateProfitAllocationTransaction(ownerId, fiscalYearId, journalId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) +
            `${ownerId}/profitAllocationDetails/fiscalYear/${fiscalYearId}/journal/${journalId}/transaction`;
        return this.http.put(url, null).pipe(map((response) => response.data));
    }
}
ProfitAllocationDetailsService.ɵfac = function ProfitAllocationDetailsService_Factory(t) { return new (t || ProfitAllocationDetailsService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
ProfitAllocationDetailsService.ɵprov = i0.ɵɵdefineInjectable({ factory: function ProfitAllocationDetailsService_Factory() { return new ProfitAllocationDetailsService(i0.ɵɵinject(i1.HttpClient)); }, token: ProfitAllocationDetailsService, providedIn: "root" });
ProfitAllocationDetailsService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ProfitAllocationDetailsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class ContactsService {
    constructor(http) {
        this.http = http;
    }
    // get related contacts. this service will return all related contacts for the given company id and contact id
    // the contact id passed in can be a client id. it should return a list of all the related contacts for the given contact id.
    getRelatedContacts(companyId, contactId, filter = '') {
        const url = UrlBase.getCoreBaseUrl(ApiVersion.v7) +
            `companies/${companyId}/contacts/${contactId}/related-contacts?pagingInfo.itemsPerPage=10000&directionOf=false&orderBy=displayAs&restrictForDirection=false${filter ? '&filterSearchSort.filter=' + filter : ''}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.items));
    }
    getRelationships(companyId, ownerId) {
        const postContent = {
            expand: 'contactExpanded',
        };
        const url = UrlBase.getCoreBaseUrl(ApiVersion.v4) + `companies/${companyId}/contacts/${ownerId}/relationships/search`;
        return (this.http
            .post(url, postContent)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.items)));
    }
    /**
     * Gets the contact for given contact id.
     * @param companyId - The company id to get the contact.
     * @param contactId - The contact id to get the contact.
     */
    getContact(companyId, contactId) {
        const url = UrlBase.getCoreBaseUrl(ApiVersion.v7) + `companies/${companyId}/contacts/${contactId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
}
ContactsService.ɵfac = function ContactsService_Factory(t) { return new (t || ContactsService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
ContactsService.ɵprov = i0.ɵɵdefineInjectable({ factory: function ContactsService_Factory() { return new ContactsService(i0.ɵɵinject(i1.HttpClient)); }, token: ContactsService, providedIn: "root" });
ContactsService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ContactsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class IntuitQboDefaultTaxGroupingMappingService {
    constructor(http) {
        this.http = http;
    }
    getDefaultMappingsByTaxYear(ownerId, taxYear) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/intuitQboDefaultTaxGroupingMapping/${taxYear}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
IntuitQboDefaultTaxGroupingMappingService.ɵfac = function IntuitQboDefaultTaxGroupingMappingService_Factory(t) { return new (t || IntuitQboDefaultTaxGroupingMappingService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
IntuitQboDefaultTaxGroupingMappingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function IntuitQboDefaultTaxGroupingMappingService_Factory() { return new IntuitQboDefaultTaxGroupingMappingService(i0.ɵɵinject(i1.HttpClient)); }, token: IntuitQboDefaultTaxGroupingMappingService, providedIn: "root" });
IntuitQboDefaultTaxGroupingMappingService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IntuitQboDefaultTaxGroupingMappingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class IntuitQboDefaultAccountGroupingMappingService {
    constructor(http) {
        this.http = http;
    }
    getDefaultAccountMappings(ownerId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/intuitAccountClassificationMapping`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
IntuitQboDefaultAccountGroupingMappingService.ɵfac = function IntuitQboDefaultAccountGroupingMappingService_Factory(t) { return new (t || IntuitQboDefaultAccountGroupingMappingService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
IntuitQboDefaultAccountGroupingMappingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function IntuitQboDefaultAccountGroupingMappingService_Factory() { return new IntuitQboDefaultAccountGroupingMappingService(i0.ɵɵinject(i1.HttpClient)); }, token: IntuitQboDefaultAccountGroupingMappingService, providedIn: "root" });
IntuitQboDefaultAccountGroupingMappingService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IntuitQboDefaultAccountGroupingMappingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class PreferencesServiceV1 {
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets application preferences
     * @param applicatiion - enum for ApplicationType
     * @param category - enum for preference category type
     * @returns application preferences
     */
    getApplicationPreferencesV1(applicatiion, category) {
        const url = UrlBase.getPreferencesUrl(ApiVersion.v1) + `?application=${applicatiion}&category=${category}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
    /**
     * Creates preferences this will only be V1 for V2 schema will create a default
     * There is no POST for V1 only
     * @param preferences - preference object
     * @returns preferences
     */
    createPreferencesV1(preferences) {
        const url = UrlBase.getPreferencesUrl(ApiVersion.v1) + `/`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, preferences).pipe(map((response) => response));
    }
    /**
     * Updates preferences
     * @param preferences - preference object passed for updating that object
     * @returns preferences
     */
    updatePreferencesV1(preferences) {
        let url = UrlBase.getPreferencesUrl(ApiVersion.v1);
        url += `/${preferences.id}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, preferences).pipe(map((response) => response));
    }
}
PreferencesServiceV1.ɵfac = function PreferencesServiceV1_Factory(t) { return new (t || PreferencesServiceV1)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
PreferencesServiceV1.ɵprov = i0.ɵɵdefineInjectable({ factory: function PreferencesServiceV1_Factory() { return new PreferencesServiceV1(i0.ɵɵinject(i1.HttpClient)); }, token: PreferencesServiceV1, providedIn: "root" });
PreferencesServiceV1.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(PreferencesServiceV1, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountMappingService {
    constructor(http) {
        this.http = http;
    }
    /**
     * A post that returns the supplied array of accounts with tax code predictions
     * @param accounts - the tax entity object that contains an array of accounts for which we want predictions
     */
    queryAccountPrediction(accounts) {
        const url = `${UrlBase.getAccountMappingUrl(ApiVersion.v1)}prediction`;
        return (this.http
            .post(url, accounts)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response)));
    }
}
AccountMappingService.ɵfac = function AccountMappingService_Factory(t) { return new (t || AccountMappingService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountMappingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountMappingService_Factory() { return new AccountMappingService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountMappingService, providedIn: "root" });
AccountMappingService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountMappingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class CategoryNodeAccountService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Assign a list of unassigned accounts to category nodes
     * @param ownerId - Owner Id
     * @param categoryNodeAccounts - List of CategoryNodeAccount
     * @returns List of assigned accounts
     */
    assignCategoryNodeAccounts(ownerId, categoryNodeAccounts) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/categoryNodeAccount`;
        return (this.http
            .post(url, categoryNodeAccounts)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    /**
     * Unassign a list of assigned accounts from category nodes
     * @param ownerId - Owner Id
     * @param categoryNodeAccountIds - List of CategoryNodeAccount Ids
     * @returns 204 (no content)
     */
    unassignCategoryNodeAccounts(ownerId, categoryNodeAccountIds) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/categoryNodeAccount/delete`;
        return this.http.post(url, categoryNodeAccountIds);
    }
}
CategoryNodeAccountService.ɵfac = function CategoryNodeAccountService_Factory(t) { return new (t || CategoryNodeAccountService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
CategoryNodeAccountService.ɵprov = i0.ɵɵdefineInjectable({ factory: function CategoryNodeAccountService_Factory() { return new CategoryNodeAccountService(i0.ɵɵinject(i1.HttpClient)); }, token: CategoryNodeAccountService, providedIn: "root" });
CategoryNodeAccountService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(CategoryNodeAccountService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TaxCodeAccountService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Assign a list of unassigned accounts to a tax code
     * @param ownerId - Owner Id
     * @param taxCodeAccounts - List of TaxCodeAccount
     * @returns List of assigned accounts
     */
    assignTaxCodeAccounts(ownerId, taxCodeAccounts) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/taxCodeAccount`;
        return (this.http
            .post(url, taxCodeAccounts)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    /**
     * Unaissign a list of assigned accounts from a tax code
     * @param ownerId - Owner Id
     * @param taxCodeAccountIds - List of TaxCodeAccount Ids
     * @returns 204 (no content)
     */
    unassignTaxCodeAccounts(ownerId, taxCodeAccountIds) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/taxCodeAccount/delete`;
        return this.http.post(url, taxCodeAccountIds);
    }
}
TaxCodeAccountService.ɵfac = function TaxCodeAccountService_Factory(t) { return new (t || TaxCodeAccountService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TaxCodeAccountService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TaxCodeAccountService_Factory() { return new TaxCodeAccountService(i0.ɵɵinject(i1.HttpClient)); }, token: TaxCodeAccountService, providedIn: "root" });
TaxCodeAccountService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TaxCodeAccountService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class DocumentUploadService {
    constructor(http) {
        this.http = http;
    }
    get formDataKey() {
        return 'file';
    }
    /**
     * Uploads Document
     * @param file -The selected File
     */
    uploadDocumentToS3(file) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + 'document-upload/temporary-storage';
        const formData = new FormData();
        formData.append(this.formDataKey, file);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, formData).pipe(map((response) => response));
    }
}
DocumentUploadService.ɵfac = function DocumentUploadService_Factory(t) { return new (t || DocumentUploadService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
DocumentUploadService.ɵprov = i0.ɵɵdefineInjectable({ factory: function DocumentUploadService_Factory() { return new DocumentUploadService(i0.ɵɵinject(i1.HttpClient)); }, token: DocumentUploadService, providedIn: "root" });
DocumentUploadService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(DocumentUploadService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountBalanceTotalService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Get account balances totals
     * @param ownerId - owner Id
     * @param accountBalanceTotalParameters - batchBalanceTotalParametersDto
     * @returns List of the account balances totals
     */
    getAccountBalanceTotals(ownerId, accountBalanceTotalParameters) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/balance/batch`;
        return this.http.post(url, accountBalanceTotalParameters).pipe(map((response) => response.data));
    }
}
AccountBalanceTotalService.ɵfac = function AccountBalanceTotalService_Factory(t) { return new (t || AccountBalanceTotalService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountBalanceTotalService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountBalanceTotalService_Factory() { return new AccountBalanceTotalService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountBalanceTotalService, providedIn: "root" });
AccountBalanceTotalService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountBalanceTotalService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountingServicesModule {
}
AccountingServicesModule.ɵfac = function AccountingServicesModule_Factory(t) { return new (t || AccountingServicesModule)(); };
AccountingServicesModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: AccountingServicesModule });
AccountingServicesModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [
        AccountService,
        AccountBalanceService,
        AccountBalanceTypeService,
        FiscalYearService,
        PeriodService,
        TaxScheduleService,
        TickmarkService,
        TrialBalanceService,
        ExternalAccountMappingService,
        YearEndCloseService,
        ProfitAllocationDetailsService,
        ContactsService,
        IntuitQboDefaultTaxGroupingMappingService,
        IntuitQboDefaultAccountGroupingMappingService,
        PreferencesServiceV1,
        AccountMappingService,
        CategoryNodeAccountService,
        TaxCodeAccountService,
        DocumentUploadService,
        AccountBalanceTotalService,
    ], imports: [[HttpClientModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountingServicesModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [HttpClientModule],
                exports: [],
                providers: [
                    AccountService,
                    AccountBalanceService,
                    AccountBalanceTypeService,
                    FiscalYearService,
                    PeriodService,
                    TaxScheduleService,
                    TickmarkService,
                    TrialBalanceService,
                    ExternalAccountMappingService,
                    YearEndCloseService,
                    ProfitAllocationDetailsService,
                    ContactsService,
                    IntuitQboDefaultTaxGroupingMappingService,
                    IntuitQboDefaultAccountGroupingMappingService,
                    PreferencesServiceV1,
                    AccountMappingService,
                    CategoryNodeAccountService,
                    TaxCodeAccountService,
                    DocumentUploadService,
                    AccountBalanceTotalService,
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(AccountingServicesModule, { imports: function () { return [HttpClientModule]; } }); })();

class DtoBase {
}

class Account extends DtoBase {
}

class AccountWithAccountGroupLinks {
}

class AccountWithTaxScheduleLinks {
}

class AccountWithLinks {
}

class CategoryNodeAccount extends DtoBase {
}

class TaxCodeAccount extends DtoBase {
}

/**
 * DTO that contains a minimal set of account details. Currently used for external account mapping purposes.
 */
class AccountDetailsV2Dto {
}

class AccountTotals extends DtoBase {
}

class AssignedCategoryNodeAccount extends DtoBase {
}

class AssignedTaxCodeAccount extends DtoBase {
}

class CategoryNodeAccountList {
}

class AccountBalanceType extends DtoBase {
}

var AccountBalanceTypeDescriptions;
(function (AccountBalanceTypeDescriptions) {
    AccountBalanceTypeDescriptions["Unadjusted"] = "Unadjusted";
    AccountBalanceTypeDescriptions["Adjusted"] = "Adjusted";
    AccountBalanceTypeDescriptions["AdjustingJE"] = "Adjusting JE";
    AccountBalanceTypeDescriptions["BeginningBalance"] = "Beginning Balance";
    AccountBalanceTypeDescriptions["Tax"] = "Tax";
    AccountBalanceTypeDescriptions["TaxJE"] = "Tax JE";
    AccountBalanceTypeDescriptions["ReclassifyingJE"] = "Reclassifying JE";
    AccountBalanceTypeDescriptions["Report"] = "Report";
    AccountBalanceTypeDescriptions["Other"] = "Other";
    AccountBalanceTypeDescriptions["OtherJE"] = "Other JE";
    AccountBalanceTypeDescriptions["ComparativeAdjustment"] = "Comparative Adjustment";
    AccountBalanceTypeDescriptions["OpeningBalance"] = "Opening Balance";
    AccountBalanceTypeDescriptions["PriorPeriodAdjustment"] = "Prior Period Adjustment (Restatement)";
})(AccountBalanceTypeDescriptions || (AccountBalanceTypeDescriptions = {}));

var AccountBalanceTypeSystemId;
(function (AccountBalanceTypeSystemId) {
    AccountBalanceTypeSystemId["Unadjusted"] = "UNADJUSTED";
    AccountBalanceTypeSystemId["Adjusted"] = "ADJUSTED";
    AccountBalanceTypeSystemId["AdjustingJE"] = "ADJUSTINGJE";
    AccountBalanceTypeSystemId["Tax"] = "TAX";
    AccountBalanceTypeSystemId["TaxJE"] = "TAXJE";
    AccountBalanceTypeSystemId["ReclassifyingJE"] = "RECLASSIFYINGJE";
    AccountBalanceTypeSystemId["Report"] = "REPORT";
    AccountBalanceTypeSystemId["Other"] = "OTHER";
    AccountBalanceTypeSystemId["OtherJE"] = "OTHERJE";
    AccountBalanceTypeSystemId["ComparativeAdjustment"] = "COMPARATIVEADJUSTMENT";
    AccountBalanceTypeSystemId["OpeningBalance"] = "OPENINGBALANCE";
    AccountBalanceTypeSystemId["PriorPeriodAdjustment"] = "PRIORPERIODADJUSTMENTRESTATEMENT";
})(AccountBalanceTypeSystemId || (AccountBalanceTypeSystemId = {}));

class AccountGroupService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets an array of the account groups by provided ownerId.
     * If fiscalYearId provided, groups will be filtered by provided fiscal year.
     * @param ownerId - Id of the owner.
     * @param fiscalYearId - The actual fiscal year. Can be empty.
     * @returns account groups.
     */
    get(ownerId, fiscalYearId) {
        let url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/accountGroup`;
        // Add fiscalYearId to the query string, if provided
        if (fiscalYearId !== undefined) {
            url += `?fiscalYearId=${fiscalYearId}`;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data.accountGroups));
    }
    /**
     * Creates new account group.
     * @param ownerId - Id of the owner.
     * @param accountGroup - The DTO wich contains data for the creation.
     * @returns Newly created account group.
     */
    post(ownerId, accountGroup) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/accountGroup`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, accountGroup).pipe(map((response) => response.data));
    }
    /**
     * Updates the account group.
     * @param ownerId - Id of the owner.
     * @param accountGroup - The DTO wich contains data for the update.
     * @returns Updated account group.
     */
    put(ownerId, accountGroup) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/accountGroup/${accountGroup.id}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, accountGroup).pipe(map((response) => response.data));
    }
    /**
     * Deletes the account group.
     * @param ownerId - Id of the owner.
     * @param accountGroup - The account group to delete.
     */
    delete(ownerId, accountGroup) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/accountGroup/delete`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, accountGroup).pipe(map((response) => response.data));
    }
}
AccountGroupService.ɵfac = function AccountGroupService_Factory(t) { return new (t || AccountGroupService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountGroupService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountGroupService_Factory() { return new AccountGroupService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountGroupService, providedIn: "root" });
AccountGroupService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountGroupService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountGroup extends DtoBase {
}

class TaxSchedule extends DtoBase {
}

// Represents the owners tax code export options
class TaxExportOption {
}

class TaxVendorService {
    constructor(http) {
        this.http = http;
    }
    getTaxVendorList(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/taxVendors`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
TaxVendorService.ɵfac = function TaxVendorService_Factory(t) { return new (t || TaxVendorService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TaxVendorService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TaxVendorService_Factory() { return new TaxVendorService(i0.ɵɵinject(i1.HttpClient)); }, token: TaxVendorService, providedIn: "root" });
TaxVendorService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TaxVendorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TaxVendor {
}

class AccountTagService {
    constructor(http) {
        this.http = http;
    }
    addAccountTags(ownerId, accountTagsToAdd) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/accountTag`;
        return this.http.post(url, accountTagsToAdd);
    }
    updateAccountTag(ownerId, accountTagToUpdate) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/accountTag/${accountTagToUpdate.id}`;
        return this.http.put(url, accountTagToUpdate);
    }
    getAccountTags(ownerId, dateToCheck) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/accountTag`;
        // check if they provided a dateToCheck, if so add to url.
        if (dateToCheck !== undefined) {
            url += `?dateToCheck=${dateToCheck.toString()}`;
        }
        return this.http.get(url);
    }
    deleteAccountTags(ownerId, accountTagIds) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/accountTag/delete`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, accountTagIds);
    }
}
AccountTagService.ɵfac = function AccountTagService_Factory(t) { return new (t || AccountTagService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountTagService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountTagService_Factory() { return new AccountTagService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountTagService, providedIn: "root" });
AccountTagService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountTagService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountTag extends DtoBase {
}

// Account tags
class AccountTagV2Dto {
}

class AccountBalance extends DtoBase {
}

class CategoryNodeService {
    constructor(http) {
        this.http = http;
    }
    get(ownerId, accountGroupId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/categoryNode/${accountGroupId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data.categoryNodes));
    }
    /**
     * Creates new category node.
     * @param ownerId - Id of the owner.
     * @param categoryNode - The DTO which contains data for the creation.
     * @returns Newly created category node.
     */
    post(ownerId, categoryNode) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/categoryNode`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, categoryNode).pipe(map((response) => response.data));
    }
    /**
     * Update the category node.
     * @param ownerId - Id of the owner.
     * @param categoryNode - The DTO which contains data for the update.
     * @returns Updated category node.
     */
    put(ownerId, categoryNode) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/categoryNode/${categoryNode.id}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, categoryNode).pipe(map((response) => response.data));
    }
    /**
     * Delete the category node.
     * @param ownerId - Id of the owner.
     * @param categoryNode - The category node to delete.
     */
    delete(ownerId, categoryNode) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/categoryNode/delete`;
        return (this.http
            .post(url, categoryNode)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data)));
    }
    getMappingOptions(ownerId, accountGroupId, excludeNoSubcode = true, reportTypes = []) {
        let url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/categoryNode/${accountGroupId}/OptionsList`;
        url += `?excludeNoSubcode=${excludeNoSubcode.toString()}`;
        reportTypes.forEach(type => {
            url += `&financialReportTypes=${type}`;
        });
        return this.http.get(url).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => {
            return response.data.map((option) => {
                return Object.assign(Object.assign({}, option), { bmaaDisplayString: `${option.codeOption} | ${option.description}` });
            });
        }));
    }
    getAllTranslatedAccountGroupCategoryNodes(ownerId, accountGroupId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/categoryNode/${accountGroupId}/translations`;
        return this.http.get(url).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => {
            return response.data.categoryNodes.map((node) => {
                const displayString = node.code && node.description
                    ? `${node.code} | ${node.description}`
                    : node.code === undefined
                        ? node.description
                        : node.code;
                return Object.assign(Object.assign({}, node), { displayString });
            });
        }));
    }
    // return the depth of a tree
    getTreeDepth(nodes) {
        let depth = 0;
        if (nodes && nodes.length > 0) {
            // iterate over the nodes and check their depths
            nodes.forEach(node => {
                const tempDepth = this.getTreeDepth(node.children);
                if (tempDepth > depth) {
                    depth = tempDepth;
                }
            });
            return 1 + depth;
        }
        return depth;
    }
    // recursive function to build a tax code tree.
    // codesNeedingChildren are the nodes at the level of the tree we're traversing. Start with nodes without parents
    // taxCodes represents the entire array of tax codes
    buildTreeRecursive(nodesNeedingChildren, allCategoryNodes) {
        nodesNeedingChildren.forEach(node => {
            // get the tax codes that are children of this code and append them to the list
            node.children = this.getChildrenForCode(node.id, allCategoryNodes);
            // if the code has children, we need to run this method recursively across the children
            if (node.children.length > 0) {
                this.buildTreeRecursive(node.children, allCategoryNodes);
            }
        });
    }
    // returns the child nodes given a code id
    getChildrenForCode(codeId, categoryNodes) {
        return categoryNodes.filter(code => {
            return code.parentId === codeId;
        });
    }
    // Returns the path for the node with id codeId
    getCodePath(nodeId, allCategoryNodes, pathArray = []) {
        // Find the code and add it to our path array at the front
        const node = allCategoryNodes.find(categoryNode => categoryNode.id === nodeId);
        if (node !== undefined) {
            pathArray.unshift(node);
        }
        if ((node === null || node === void 0 ? void 0 : node.parentId) !== undefined && node.parentId.length > 0) {
            return this.getCodePath(node.parentId, allCategoryNodes, pathArray);
        }
        return pathArray;
    }
    // Returns updated sequence number of category nodes
    updateSequenceNumberCategoryNode(ownerId, orderElement) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/categoryNode/${orderElement.id}/sequence`;
        return (this.http
            .put(url, orderElement)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data.categoryNodes)));
    }
}
CategoryNodeService.ɵfac = function CategoryNodeService_Factory(t) { return new (t || CategoryNodeService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
CategoryNodeService.ɵprov = i0.ɵɵdefineInjectable({ factory: function CategoryNodeService_Factory() { return new CategoryNodeService(i0.ɵɵinject(i1.HttpClient)); }, token: CategoryNodeService, providedIn: "root" });
CategoryNodeService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(CategoryNodeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class CategoryNode extends DtoBase {
}

class CategoryNodeOption {
}

var FinancialReportTypes;
(function (FinancialReportTypes) {
    FinancialReportTypes["Asset"] = "A";
    FinancialReportTypes["Equity"] = "Q";
    FinancialReportTypes["Liability"] = "L";
    FinancialReportTypes["Expense"] = "E";
    FinancialReportTypes["Revenue"] = "R";
    FinancialReportTypes["Memo"] = "M";
})(FinancialReportTypes || (FinancialReportTypes = {}));

var TotalBalanceCondition;
(function (TotalBalanceCondition) {
    TotalBalanceCondition[TotalBalanceCondition["AllBalances"] = 1] = "AllBalances";
    TotalBalanceCondition[TotalBalanceCondition["CreditBalances"] = 2] = "CreditBalances";
    TotalBalanceCondition[TotalBalanceCondition["DebitBalances"] = 3] = "DebitBalances";
})(TotalBalanceCondition || (TotalBalanceCondition = {}));

var FinancialReportCategoryId;
(function (FinancialReportCategoryId) {
    FinancialReportCategoryId[FinancialReportCategoryId["CurrentAssets"] = 1] = "CurrentAssets";
    FinancialReportCategoryId[FinancialReportCategoryId["NetPropertyAndEquipment"] = 2] = "NetPropertyAndEquipment";
    FinancialReportCategoryId[FinancialReportCategoryId["NonCurrentAssets"] = 3] = "NonCurrentAssets";
    FinancialReportCategoryId[FinancialReportCategoryId["CurrentLiabilities"] = 4] = "CurrentLiabilities";
    FinancialReportCategoryId[FinancialReportCategoryId["LongTermLiabilities"] = 5] = "LongTermLiabilities";
    FinancialReportCategoryId[FinancialReportCategoryId["Equity"] = 6] = "Equity";
    FinancialReportCategoryId[FinancialReportCategoryId["Sales"] = 7] = "Sales";
    FinancialReportCategoryId[FinancialReportCategoryId["CostOfSales"] = 8] = "CostOfSales";
    FinancialReportCategoryId[FinancialReportCategoryId["OperatingExpenses"] = 9] = "OperatingExpenses";
    FinancialReportCategoryId[FinancialReportCategoryId["OtherIncome"] = 10] = "OtherIncome";
    FinancialReportCategoryId[FinancialReportCategoryId["OtherExpenses"] = 11] = "OtherExpenses";
    FinancialReportCategoryId[FinancialReportCategoryId["InterestExpense"] = 12] = "InterestExpense";
    FinancialReportCategoryId[FinancialReportCategoryId["ProvisionForIncomeTaxes"] = 13] = "ProvisionForIncomeTaxes";
    FinancialReportCategoryId[FinancialReportCategoryId["Memo"] = 14] = "Memo";
    FinancialReportCategoryId[FinancialReportCategoryId["ArCurrentAssets"] = 15] = "ArCurrentAssets";
    FinancialReportCategoryId[FinancialReportCategoryId["ArNonCurrentAssets"] = 16] = "ArNonCurrentAssets";
    FinancialReportCategoryId[FinancialReportCategoryId["ArCurrentLiabilities"] = 17] = "ArCurrentLiabilities";
    FinancialReportCategoryId[FinancialReportCategoryId["ArLongTermLiabilities"] = 18] = "ArLongTermLiabilities";
    FinancialReportCategoryId[FinancialReportCategoryId["ArEquity"] = 19] = "ArEquity";
    FinancialReportCategoryId[FinancialReportCategoryId["ArReserves"] = 20] = "ArReserves";
    FinancialReportCategoryId[FinancialReportCategoryId["ArRetainedEarnings"] = 21] = "ArRetainedEarnings";
    FinancialReportCategoryId[FinancialReportCategoryId["ArOperatingIncome"] = 22] = "ArOperatingIncome";
    FinancialReportCategoryId[FinancialReportCategoryId["ArOperatingExpenses"] = 23] = "ArOperatingExpenses";
    FinancialReportCategoryId[FinancialReportCategoryId["ArOtherIncome"] = 24] = "ArOtherIncome";
    FinancialReportCategoryId[FinancialReportCategoryId["ArOtherExpenses"] = 25] = "ArOtherExpenses";
})(FinancialReportCategoryId || (FinancialReportCategoryId = {}));

class CompanyService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Get the full company information
     * @param companyId - The id of the company
     */
    getCompanyInfo(companyId) {
        return this.http
            .get(`${UrlBase.getCoreBaseUrl(ApiVersion.v3)}/companies/${companyId}/main`)
            .pipe(map((response) => response));
    }
    /**
     * Get the client for a specific company
     * @param companyId - The id of the company.
     */
    getCompanyClient(companyId) {
        return this.http.get(`${UrlBase.getCoreBaseUrl(ApiVersion.v3)}/companies/${companyId}/main`).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => response.companyClient));
    }
}
CompanyService.ɵfac = function CompanyService_Factory(t) { return new (t || CompanyService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
CompanyService.ɵprov = i0.ɵɵdefineInjectable({ factory: function CompanyService_Factory() { return new CompanyService(i0.ɵɵinject(i1.HttpClient)); }, token: CompanyService, providedIn: "root" });
CompanyService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(CompanyService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class CompanyClient extends DtoBase {
}

class CompanyInfo {
}

class CompanyOwnerContact {
}

var ApplicationType;
(function (ApplicationType) {
    ApplicationType["Accounts"] = "Accounts";
    ApplicationType["CheckpointEngage"] = "CheckpointEngage";
    ApplicationType["TrialBalanceStandalone"] = "TrialBalanceStandalone";
    ApplicationType["Workpapers"] = "Workpapers";
})(ApplicationType || (ApplicationType = {}));

/**
 * Country Code for determining Locale
 * Matches up with locales for the locale on the IEnvironment from blue/services environment.getEnvironmentAsync()
 */
var CountryCodes;
(function (CountryCodes) {
    CountryCodes["UK"] = "GB";
    CountryCodes["CA"] = "CA";
    CountryCodes["US"] = "US";
    CountryCodes["AR"] = "AR";
})(CountryCodes || (CountryCodes = {}));

var ApplicationTypeId;
(function (ApplicationTypeId) {
    ApplicationTypeId["OnvioAccounts"] = "ONVIOACCOUNTS";
    ApplicationTypeId["Workpapers"] = "WORKPAPERS";
    ApplicationTypeId["USFixedAssets"] = "USFIXEDASSETS";
    ApplicationTypeId["USTax"] = "USTAX";
    ApplicationTypeId["USAdvisoryTools"] = "USADVISORYTOOLS";
})(ApplicationTypeId || (ApplicationTypeId = {}));

class FinancialReportCategoryService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Returns financial report categories for specific owner
     * @param ownerId - Owner Id
     * @returns Array of FinancialReportCategory
     */
    getFinancialReportCategories(ownerId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/financialreportcategories/translations`;
        return (this.http
            .get(url)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response.data.financialreportcategories)));
    }
}
FinancialReportCategoryService.ɵfac = function FinancialReportCategoryService_Factory(t) { return new (t || FinancialReportCategoryService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
FinancialReportCategoryService.ɵprov = i0.ɵɵdefineInjectable({ factory: function FinancialReportCategoryService_Factory() { return new FinancialReportCategoryService(i0.ɵɵinject(i1.HttpClient)); }, token: FinancialReportCategoryService, providedIn: "root" });
FinancialReportCategoryService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FinancialReportCategoryService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class FinancialReportCategory extends DtoBase {
}

class FinancialReportDetail extends DtoBase {
}

class FiscalYear extends DtoBase {
}

var FrequencyType;
(function (FrequencyType) {
    FrequencyType[FrequencyType["Annual"] = 1] = "Annual";
    FrequencyType[FrequencyType["SemiAnnual"] = 2] = "SemiAnnual";
    FrequencyType[FrequencyType["Quarterly"] = 3] = "Quarterly";
    FrequencyType[FrequencyType["Monthly"] = 4] = "Monthly";
    FrequencyType[FrequencyType["Weekly"] = 5] = "Weekly";
    FrequencyType[FrequencyType["ThirteenPeriods"] = 6] = "ThirteenPeriods";
    FrequencyType[FrequencyType["Custom"] = 7] = "Custom";
})(FrequencyType || (FrequencyType = {}));

class Period extends DtoBase {
}

var PeriodType;
(function (PeriodType) {
    PeriodType[PeriodType["Accounting"] = 1] = "Accounting";
    PeriodType[PeriodType["Practice"] = 2] = "Practice";
    PeriodType[PeriodType["FixedAssets"] = 3] = "FixedAssets";
})(PeriodType || (PeriodType = {}));

class TaxCodeService {
    constructor(http) {
        this.http = http;
    }
    get(ownerId, taxScheduleId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/taxCode/${taxScheduleId}`;
        return this.http.get(url).pipe(map((response) => {
            return response.data.map((code) => {
                const displayString = code.code !== undefined && code.description !== undefined
                    ? `${code.code} | ${code.description}`
                    : code.code === undefined
                        ? code.description
                        : code.code;
                return Object.assign(Object.assign({}, code), { displayString });
            });
        }));
    }
    /**
     * Creates new tax code.
     * @param ownerId - Id of the owner.
     * @param taxCode - The DTO which contains data for the creation.
     * @returns Newly created tax code.
     */
    post(ownerId, taxCode) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/TaxCode`;
        return this.http.post(url, taxCode).pipe(map((response) => response.data));
    }
    /**
     * Update the tax code.
     * @param ownerId - Id of the owner.
     * @param taxCode - The DTO which contains data for the update.
     * @returns Updated tax code.
     */
    put(ownerId, taxCode) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/TaxCode/${taxCode.id}`;
        return this.http.put(url, taxCode).pipe(map((response) => response.data));
    }
    /**
     * Delete the tax code.
     * @param ownerId - Id of the owner.
     * @param taxCodeId - Id of the tax code to delete.
     */
    delete(ownerId, taxCodeId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/TaxCode/${taxCodeId}`;
        return this.http
            .delete(url)
            .pipe(map((response) => response.data));
    }
    getMappingOptions(ownerId, taxScheduleId, excludeNoSubcode = true) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/taxSchedule/${taxScheduleId}/OptionsList?excludeNoSubcode=${excludeNoSubcode.toString()}`;
        return this.http.get(url).pipe(map((response) => {
            return response.data.map((option) => {
                return Object.assign(Object.assign({}, option), { bmaaDisplayString: `${option.codeOption} | ${option.description}` });
            });
        }));
    }
    /**
     * This method gives us TaxCode in sequence order
     * like
     * TaxCode1
     *    subcode1Level1
     *      subcode1Level2
     *    subcode2Level1
     *      subcode1Level2
     * TaxCode2
     *    subcode1Level1
     *      subcode1Level2
     *    subcode2Level1
     *      subcode1Level2
     * @param ownerId - the id of the owner
     * @param taxScheduleId - The TaxSchedule Id for the desired tax schedule
     * @param excludeNoSubcode - If it is true, we need to exclude the No Subcode from the option list.
     */
    getSortedMappingOptions(ownerId, taxScheduleId, excludeNoSubcode = true) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v2)}${ownerId}/tax-schedule/${taxScheduleId}`;
        url += `/sorted-options-list?excludeNoSubcode=${excludeNoSubcode.toString()}`;
        return this.http.get(url).pipe(map((response) => {
            return response.data.map((option) => {
                return Object.assign(Object.assign({}, option), { bmaaDisplayString: `${option.codeOption} | ${option.description}` });
            });
        }));
    }
    // return the depth of a tree
    getTreeDepth(nodes) {
        let depth = 0;
        if (nodes && nodes.length > 0) {
            // iterate over the nodes and check their depths
            nodes.forEach(node => {
                const children = node.children.filter(code => code.unit === undefined);
                const tempDepth = this.getTreeDepth(children);
                if (tempDepth > depth) {
                    depth = tempDepth;
                }
            });
            return 1 + depth;
        }
        return depth;
    }
    // recursive function to build a tax code tree.
    // codesNeedingChildren are the nodes at the level of the tree we're traversing. Start with nodes without parents
    // taxCodes represents the entire array of tax codes
    buildTreeRecursive(codesNeedingChildren, allTaxCodes) {
        codesNeedingChildren.forEach(node => {
            // get the tax codes that are children of this code and append them to the list
            node.children = this.getChildrenForCode(allTaxCodes, node.id);
            // if the code has children, we need to run this method recursively across the children
            if (node.children.length > 0) {
                this.buildTreeRecursive(node.children, allTaxCodes);
            }
        });
    }
    // Gets all the nodes from taxCodes that are children of codeId
    getChildrenForCode(taxCodes, codeId) {
        return taxCodes.filter(code => {
            return code.parentId === codeId;
        });
    }
    // Returns the path for the code with id codeId
    getCodePath(codeId, allTaxCodes, pathArray = []) {
        // Find the code and add it to our path array at the front
        const code = allTaxCodes.find(taxCode => taxCode.id === codeId);
        if (code !== undefined) {
            pathArray.unshift(code);
        }
        if ((code === null || code === void 0 ? void 0 : code.parentId) !== undefined && code.parentId.length > 0) {
            return this.getCodePath(code.parentId, allTaxCodes, pathArray);
        }
        return pathArray;
    }
    // Returns updated sequence number of tax codes
    updateSequenceNumberTaxCode(ownerId, orderElement) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/TaxCode/${orderElement.id}/sequence`;
        return this.http
            .put(url, orderElement)
            .pipe(map((response) => response.data.categoryNodes));
    }
}
TaxCodeService.ɵfac = function TaxCodeService_Factory(t) { return new (t || TaxCodeService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TaxCodeService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TaxCodeService_Factory() { return new TaxCodeService(i0.ɵɵinject(i1.HttpClient)); }, token: TaxCodeService, providedIn: "root" });
TaxCodeService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TaxCodeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TaxCode extends DtoBase {
}

class TaxCodeOption {
}

var CurrencyTypeEnum;
(function (CurrencyTypeEnum) {
    CurrencyTypeEnum["ARS"] = "ARS";
    CurrencyTypeEnum["CAD"] = "CAD";
    CurrencyTypeEnum["GBP"] = "GBP";
    CurrencyTypeEnum["USD"] = "USD";
})(CurrencyTypeEnum || (CurrencyTypeEnum = {}));

class AmountFormattingService {
    /**
     * Returns the formatted amount as per language passed
     * @param language - language/ locale to be used for formatting
     * @param amount - amounts that needs to be formatted
     */
    formatAmount(language, amount) {
        const options = {
            style: 'currency',
            minimumFractionDigits: 2,
        };
        // currency code from: https://www.currency-iso.org/en/home/tables/table-a1.html
        switch (language) {
            case 'es-ar':
                options.currency = CurrencyTypeEnum.ARS;
                break;
            case 'en-ca':
                options.currency = CurrencyTypeEnum.CAD;
                break;
            case 'fr-ca':
                options.currency = 'CAD';
                options.currencyDisplay = 'narrowSymbol';
                break;
            case 'en-gb':
                options.currency = CurrencyTypeEnum.GBP;
                break;
            default:
                options.currency = CurrencyTypeEnum.USD;
        }
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
        return new Intl.NumberFormat(language, options).format(amount);
    }
}
AmountFormattingService.ɵfac = function AmountFormattingService_Factory(t) { return new (t || AmountFormattingService)(); };
AmountFormattingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AmountFormattingService_Factory() { return new AmountFormattingService(); }, token: AmountFormattingService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AmountFormattingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

class Tickmark extends DtoBase {
}

var Country;
(function (Country) {
    Country["us"] = "us";
})(Country || (Country = {}));

var Product;
(function (Product) {
    Product["Workpapers"] = "Workpapers";
    Product["TrialBalanceStandalone"] = "TrialBalanceStandalone";
})(Product || (Product = {}));

var IncludeType;
(function (IncludeType) {
    IncludeType[IncludeType["PeriodOnly"] = 0] = "PeriodOnly";
    IncludeType[IncludeType["BeginningBalance"] = 1] = "BeginningBalance";
    IncludeType[IncludeType["PriorPeriods"] = 2] = "PriorPeriods";
    IncludeType[IncludeType["FullYear"] = 3] = "FullYear";
    IncludeType[IncludeType["BeginningBalanceAndPriorPeriods"] = 4] = "BeginningBalanceAndPriorPeriods";
    IncludeType[IncludeType["BeginningBalanceAndFullYear"] = 5] = "BeginningBalanceAndFullYear";
})(IncludeType || (IncludeType = {}));

class TrialBalanceAccount extends AccountWithLinks {
}

class TrialBalanceColumnValue {
}

class TrialBalanceGridData {
}

class TrialBalanceRow {
}

/**
 * The trialBalanceColAmount that goes on balance columns, tickmark columns and reference columns
 */
class TrialBalanceColumnAmount extends DtoBase {
}

/**
 * The ValueType for a Trial Balance Column
 */
var ValueType;
(function (ValueType) {
    ValueType[ValueType["Balance"] = 1] = "Balance";
    ValueType[ValueType["Debit"] = 2] = "Debit";
    ValueType[ValueType["Credit"] = 3] = "Credit";
    ValueType[ValueType["Description"] = 4] = "Description";
    ValueType[ValueType["AccountNumber"] = 5] = "AccountNumber";
    ValueType[ValueType["Code"] = 6] = "Code";
    ValueType[ValueType["Units"] = 7] = "Units";
    ValueType[ValueType["FinancialReportCategory"] = 8] = "FinancialReportCategory";
    ValueType[ValueType["FinancialReportCategoryType"] = 9] = "FinancialReportCategoryType";
    ValueType[ValueType["FinancialReportDetail"] = 10] = "FinancialReportDetail";
    ValueType[ValueType["FinancialReportCategoryAndDetail"] = 11] = "FinancialReportCategoryAndDetail";
    ValueType[ValueType["Comments"] = 12] = "Comments";
    ValueType[ValueType["WorkpaperReference"] = 13] = "WorkpaperReference";
    ValueType[ValueType["AccountGroup"] = 14] = "AccountGroup";
    ValueType[ValueType["TaxSchedule"] = 15] = "TaxSchedule";
    ValueType[ValueType["Tickmark"] = 16] = "Tickmark";
    ValueType[ValueType["Dynamic"] = 17] = "Dynamic";
    ValueType[ValueType["NetDebit"] = 18] = "NetDebit";
    ValueType[ValueType["NetCredit"] = 19] = "NetCredit";
    ValueType[ValueType["AccountKey"] = 112] = "AccountKey";
})(ValueType || (ValueType = {}));

/**
 * DtoBase to be used with newer models that return their values in capital case
 */
class DtoBaseCapitalCase {
}

/**
 * Model for a Trial Balance Column on the Trial Balance Grid
 */
class TrialBalanceColumn extends DtoBaseCapitalCase {
}

/**
 * Source Type for the Trial Balance Grid
 */
var TrialBalanceGridSourceType;
(function (TrialBalanceGridSourceType) {
    TrialBalanceGridSourceType[TrialBalanceGridSourceType["Account"] = 1] = "Account";
    TrialBalanceGridSourceType[TrialBalanceGridSourceType["AccountGroup"] = 2] = "AccountGroup";
    TrialBalanceGridSourceType[TrialBalanceGridSourceType["TaxCode"] = 3] = "TaxCode";
})(TrialBalanceGridSourceType || (TrialBalanceGridSourceType = {}));

/**
 * Model for a Trial Balance Grid
 */
class TrialBalanceGrid extends DtoBase {
}

/**
 *  TrialBalanceGridDataParams
 * Trial balance grid data params value passed in to get the trial balance grid data service call
 */
class TrialBalanceGridDataParams {
}

/**
 * DTO for updating a Trial Balance Column Value
 */
class UpdateTrialBalanceColumnValueDto {
}

class ExternalAccountMapping extends DtoBase {
}

class AccountMapping {
}

class AccountDetails {
}

class QboAccountDetailsForQboMapping extends DtoBase {
}

class QboExternalAccountMapping extends DtoBase {
}

class OnvioAccountDetailsForQboMapping extends Account {
}

class OnvioAccountMappingToQboAccount extends DtoBase {
}

/**
 * DTO for a set of external account mappings to Onvio accounts
 */
class ExternalAccountMappingV2Dto {
}

/**
 * DTO for an individual external account to Onvio account mapping.
 */
class AccountToExternalAccountMappingV2Dto {
}

var ExternalAccountMappingType;
(function (ExternalAccountMappingType) {
    ExternalAccountMappingType[ExternalAccountMappingType["Spreadsheet"] = 1] = "Spreadsheet";
    ExternalAccountMappingType[ExternalAccountMappingType["QuickBooksOnline"] = 2] = "QuickBooksOnline";
})(ExternalAccountMappingType || (ExternalAccountMappingType = {}));

class ContactRelationshipExpansionV4Dto {
}
class NamedReferenceDto {
}

class Contact {
}

class ProfitAllocationDetails extends DtoBase {
}

class FixedProfitShare {
}

// It contains relation between Account and ContraAccount for a fiscal year and owner
// that can be used for profit allocation in associated fiscal year
class ProfitAllocation {
}

// For collection of balances and open balances of account.
// Also it include Category Nodes and tax codes mapping with the account
class ImportAccountsWithBalances extends AccountWithLinks {
}

// Importing the accounts, each account contains collection of balances and out of balances will be collected and import the list
class ImportV3Dto {
}

// Account with out of balance amount, also receving a balance account with
// the out of balance amount either on credit or debit.
class ImportAccountsWithOutOfBalanceAccount {
}

// The opening balance to include for an account import
class OpenBalance {
}

class ImportAccountService {
    constructor(http) {
        this.http = http;
    }
    // Submit the collection of accounts having balances and open balances
    importAccounts(ownerId, importV3Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccounts`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, importV3Dto).pipe(map((response) => response));
    }
    // Submits the collection of accounts having out of balance of credit or debit
    importAccountsWithOutOfBalance(ownerId, importOutOfBalancesAccounts) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsWithOutOfBalance`;
        return (this.http
            .post(url, importOutOfBalancesAccounts)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response)));
    }
    /**
     * Submit the collection of accounts having balances and open balances and create balances as transaction.
     * @param ownerId - The owner id for the import
     * @param importV3Dto - The import accounts payload
     */
    importAccountsAsTransaction(ownerId, importV3Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsAsTransaction`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, importV3Dto).pipe(map((response) => response));
    }
    /**
     * Submits the collection of accounts having out of balance of credit or debit and create balances as transaction.
     * @param ownerId - The owner id for the import
     * @param importOutOfBalancesAccounts - The import out of balance accounts payload
     */
    importAccountsWithOutOfBalanceAsTransaction(ownerId, importOutOfBalancesAccounts) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsWithOutOfBalanceAsTransaction`;
        return (this.http
            .post(url, importOutOfBalancesAccounts)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response)));
    }
    /**
     * Import the accounts having balances and open balances and with or without document link for matching owner
     * @param ownerId - The ID of the owner to import the accounts and balances into.
     * @param importAccountsWithDocumentLinkV1Dto - ImportAccountsWithDocumentLinkV1Dto containing the list of accounts need to be imported
     * with import document link
     * @returns The accepted status of the import
     */
    importAccountsQueued(ownerId, importAccountsWithDocumentLinkV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsQueued`;
        return this.http.post(url, importAccountsWithDocumentLinkV1Dto).pipe(map((response) => response));
    }
    /**
     * Import the accounts having  accounts having out of balance of credit or debit and with or without document link for matching owner.
     * @param ownerId - The ID of the owner to import the accounts and balances into.
     * @param importAccountsWithOutOfBalanceWithDocumentLinkV1Dto - ImportAccountsWithOutOfBalanceWithDocumentLinkV1Dto
     * containing The list of accounts need to be imported with document link and the balancing account with the
     * out of balance amount and AccountTransactionSourceType
     * @returns The accepted status of the import
     */
    importAccountsWithOutOfBalanceQueued(ownerId, importAccountsWithOutOfBalanceWithDocumentLinkV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsWithOutOfBalanceQueued`;
        return this.http
            .post(url, importAccountsWithOutOfBalanceWithDocumentLinkV1Dto)
            .pipe(map((response) => response));
    }
    /**
     * Import accounts with links, TaxCodeAccounts, CategoryNodeAccounts and balances and with or without document link for matching owner.
     * Stores the import account balance by adding/subtracting from existing balances rather than importing as new balances.
     * For example : if old balance is 50 and new balance is 100. The result should be new balance is 150 and Transaction is 100.
     * @param ownerId - The ID of the owner to import the accounts and balances into.
     * @param importAccountsWithDocumentLinkV1Dto - ImportAccountsWithDocumentLinkV1Dto containing the list of accounts need to be imported
     * with import document link
     * @returns The accepted status of the import
     */
    importAccountsAsTransactionQueued(ownerId, importAccountsWithDocumentLinkV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsAsTransactionQueued`;
        return this.http.post(url, importAccountsWithDocumentLinkV1Dto).pipe(map((response) => response));
    }
    /**
     * Import a list of accounts setting up the balance amount as transaction in a balancing account
     * Returning actual value of balances in transactions not movement
     * @param ownerId - The ID of the owner to import the accounts and balances into.
     * @param importAccountsWithOutOfBalanceWithDocumentLinkV1Dto - ImportAccountsWithOutOfBalanceWithDocumentLinkV1Dto
     * containing The list of accounts need to be imported with document link and the balancing account with the
     * out of balance amount and AccountTransactionSourceType
     *  @returns The accepted status of the import
     */
    importAccountsWithOutOfBalanceAsTransactionQueued(ownerId, importAccountsWithOutOfBalanceWithDocumentLinkV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v3) + `${ownerId}/importAccountsWithOutOfBalanceAsTransactionQueued`;
        return this.http
            .post(url, importAccountsWithOutOfBalanceWithDocumentLinkV1Dto)
            .pipe(map((response) => response));
    }
}
ImportAccountService.ɵfac = function ImportAccountService_Factory(t) { return new (t || ImportAccountService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
ImportAccountService.ɵprov = i0.ɵɵdefineInjectable({ factory: function ImportAccountService_Factory() { return new ImportAccountService(i0.ɵɵinject(i1.HttpClient)); }, token: ImportAccountService, providedIn: "root" });
ImportAccountService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ImportAccountService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

var AccountTransactionSourceType;
(function (AccountTransactionSourceType) {
    AccountTransactionSourceType[AccountTransactionSourceType["SpreadsheetImport"] = 1] = "SpreadsheetImport";
    AccountTransactionSourceType[AccountTransactionSourceType["QuickBooksOnline"] = 2] = "QuickBooksOnline";
    AccountTransactionSourceType[AccountTransactionSourceType["DataEntry"] = 3] = "DataEntry";
    AccountTransactionSourceType[AccountTransactionSourceType["OnBalance"] = 4] = "OnBalance";
    AccountTransactionSourceType[AccountTransactionSourceType["UnknownSource"] = 5] = "UnknownSource";
    AccountTransactionSourceType[AccountTransactionSourceType["SystemCreated"] = 6] = "SystemCreated";
    AccountTransactionSourceType[AccountTransactionSourceType["SpreadsheetTransactionsImport"] = 7] = "SpreadsheetTransactionsImport";
    AccountTransactionSourceType[AccountTransactionSourceType["Zapier"] = 8] = "Zapier";
})(AccountTransactionSourceType || (AccountTransactionSourceType = {}));

// Accounts with out of balance amount.
// This DTO receives a balancing account with the out of balance amount
// either on credit or debit. It also receives an import account list.
class ImportOutOfBalancesAccounts {
}

/**
 * DTO contains ImportV3Dto object with import document link
 */
class ImportAccountsWithDocumentLinkV1Dto {
}

/**
 * DTO contains ImportOutOfBalancesAccounts object with import document link
 */
class ImportAccountsWithOutOfBalanceWithDocumentLinkV1Dto {
}

// Class used for sorting
class SortingService {
    /**
     * It sorts the values in ascending order as per below sequence -
     * First, the integers values, then alphaNumeric/string values and then null/empty values
     * @param param0 - values to be sorted
     */
    alphaNumericSortAscending({ a, b }) {
        if (a === b) {
            return 0;
        }
        if (!a) {
            return 1;
        }
        if (!b) {
            return -1;
        }
        if (!isNaN(Number(a)) && !isNaN(Number(b))) {
            return Number(a) > Number(b) ? 1 : -1;
        }
        if (!isNaN(Number(a)) && isNaN(Number(b))) {
            return -1;
        }
        if (isNaN(Number(a)) && !isNaN(Number(b))) {
            return 1;
        }
        if (isNaN(Number(a)) && isNaN(Number(b))) {
            return a > b ? 1 : -1;
        }
    }
    /**
     * Sorts strings in ascending order.
     * `Intl.Collator` is used for string comparsion, with next options: `{ sensitivity: 'accent' }`
     * @param first - The first string for comparsion.
     * @param second - The second string for comparsion.
     * @param locale - The locale code for the `Intl.Collator`. An optional parameter.
     * @returns a number indicating how `first` and `second` compare to each other
     *
     * `0` - if strings are considered equal or both are `undefined`,
     *
     * `positive` - if `first` string comes after `second` or `second` is `undefined`,
     *
     * `negative` - if `first` string comes before `second` or `first` is `undefined`
     */
    caseInsesitiveSortAscending(first, second, locale) {
        // if both values are present pass them to the Intl.Collation.compare method.
        if (Boolean(first) && Boolean(second)) {
            return Intl.Collator(locale, { sensitivity: 'accent' }).compare(first, second);
        }
        // if both values are undefined, we consider them as equal.
        if (!first && !second) {
            return 0;
        }
        // If previous steps are passed it means that only one value is undefined.
        // If it's first, we consider that it comes after second.
        if (!first) {
            return 1;
        }
        // if it's second, we consider that first comes before second.
        if (!second) {
            return -1;
        }
    }
    /**
     * Custom alphabetic tickmark description sort copied from legacy
     * @param param0 - object with two descriptions
     * @returns number
     */
    descriptionSort({ as, bs }) {
        let a1;
        let b1;
        let i = 0;
        let n;
        const rx = /(\.\d+)|(\d+(\.\d+)?)|([^\d.]+)|(\.\D+)|(\.$)/g;
        if (as === bs) {
            return 0;
        }
        const a = as.toLowerCase().match(rx);
        const b = bs.toLowerCase().match(rx);
        const L = a.length;
        while (i < L) {
            if (!b[i]) {
                return 1;
            }
            a1 = a[i];
            b1 = b[i++];
            if (a1 !== b1) {
                n = a1 - b1;
                if (!isNaN(n)) {
                    return n;
                }
                return a1 > b1 ? 1 : -1;
            }
        }
        return b[i] ? -1 : 0;
    }
    /**
     * Sort comparer method that uses a combination of alpha and numeric sorts based on string chunks.  This is not a straight alphanumeric sort
     * @param left - left comparison string
     * @param right - right comparison string
     * @returns - -1, 0, 1 based on equality
     */
    sortAlphaNum(left, right) {
        /**
         * Splits a string into an array of chunks to be used for sorting
         * Chunks are defined as a grouping of adjoining characters that are either:
         *   a) numbers and/or decimals
         *   b) characters other than numbers & decimals
         * @param item - string to split into chunks
         * @returns - array of chunks
         */
        function chunkify(item) {
            const chunkArray = [];
            let charIndex = 0;
            let chunkIndex = 0;
            let wasDotOrNumber;
            let firstPass = true;
            let currentCharacterCode;
            do {
                const currentCharacter = item.charAt(charIndex++);
                currentCharacterCode = currentCharacter.charCodeAt(0);
                // determine if the char is a dot or number (ascii chars 46 and 48-57)
                const isDotOrNumber = currentCharacterCode === 46 || (currentCharacterCode >= 48 && currentCharacterCode <= 57);
                if (firstPass) {
                    wasDotOrNumber = isDotOrNumber;
                }
                else if (isDotOrNumber !== wasDotOrNumber) {
                    wasDotOrNumber = isDotOrNumber;
                    chunkIndex++;
                }
                if (chunkArray[chunkIndex] === undefined) {
                    chunkArray[chunkIndex] = '';
                }
                chunkArray[chunkIndex] += currentCharacter;
                firstPass = false;
            } while (currentCharacterCode);
            return chunkArray;
        }
        const leftChunks = chunkify(left.toLowerCase());
        const rightChunks = chunkify(right.toLowerCase());
        for (let x = 0; leftChunks[x] && rightChunks[x]; x++) {
            if (leftChunks[x] !== rightChunks[x]) {
                const leftNum = Number(leftChunks[x]);
                const rightNum = Number(rightChunks[x]);
                if (leftNum.toString() === leftChunks[x] && rightNum.toString() === rightChunks[x]) {
                    return leftNum > rightNum ? 1 : -1;
                }
                return leftChunks[x] > rightChunks[x] ? 1 : -1;
            }
        }
        return leftChunks.length - rightChunks.length;
    }
}
SortingService.ɵfac = function SortingService_Factory(t) { return new (t || SortingService)(); };
SortingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function SortingService_Factory() { return new SortingService(); }, token: SortingService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SortingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

class TransDistV1Dto extends DtoBase {
}

class TransactionSubTypeV1Dto {
}

class TransactionV2Dto {
}

class TransactionV1Dto {
}

/**
 * A model that contains base data for workpaper reference
 *
 */
class WorkpaperReference extends DtoBase {
}

// Model for workpaper reference values associated with a transaction
class TransactionWorkpaperReferenceV1Dto extends WorkpaperReference {
}

class TransactionService {
    /**
     * constructor for TransactionService class
     * @param httpClient - object of HttpCLient
     */
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /**
     * Adds the transaction for matching owner
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param transactionDto - TransactionDto containing the transaction and transaction distributions
     * @returns The added transaction
     */
    addTransaction(ownerId, transactionDto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/transaction`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.post(url, transactionDto).pipe(map((response) => response));
    }
    /**
     * Update an existing transaction or create a new transaction from a source Id. Distributions must be provided for the add to be
     * successful. When the transaction type of a transaction is changed then all distributions must be provided. If a distribution is
     * changed to a new account then it will be deleted and replaced with a new distribution due to logic for updating the related balances
     * If the transaction doesn't find a matching source ID it will create a new transaction.
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param sourceId - The source ID of the transaction being updated, or added if it doesn't already exist
     * @param transactionDto - TransactionDto containing the transaction and transaction distributions
     * @returns The updated or added transaction
     */
    addOrUpdateTransactionFromSourceId(ownerId, sourceId, transactionDto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/sourceTransaction/${sourceId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.put(url, transactionDto).pipe(map((response) => response));
    }
    /**
     * Gets all corresponding transacitons for matching JournalId. Returns translations for all defined translatable descriptions.
     * @param ownerId - The OwnerId of the requested transactions
     * @param journalId - The ID of the Journal that the transactions are member of
     * @returns All transactions for corresponding ownerID and JournalId
     */
    getAllTranslatedTransactions(ownerId, journalId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/transaction/${journalId}/translated`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.get(url).pipe(map((response) => response));
    }
    /**
     * Gets all corresponding transacitons for matching JournalId.
     * @param ownerId - The OwnerId of the requested transactions
     * @param journalId - The ID of the Journal that the transactions are member of
     * @returns All transactions for corresponding ownerID and JournalId
     */
    getAllTransactions(ownerId, journalId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/transaction/${journalId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.get(url).pipe(map((response) => response));
    }
    /**
     * Gets the corresponding transaction for matching  ownerID, JournalId and transactionId.
     * @param ownerId - The OwnerId of the requested transaction
     * @param journalId - The ID of the Journal that the transaction is member of
     * @param transactionId - The transactionId for the transaction
     * @returns Transactions for corresponding ownerID, JournalId and transactionId
     */
    getTransaction(ownerId, journalId, transactionId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/transaction/${journalId}/${transactionId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.get(url).pipe(map((response) => response));
    }
    /**
     * Gets the count of transactions for the specified owner.
     * @param ownerId - The OwnerId.
     * @returns The count of transactions for the specified owner.
     */
    getTransactionCount(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/transactionCount`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.get(url).pipe(map((response) => response.data.transactionCount));
    }
    /**
     *  Get the corresponding transaction for matching source ID and all of it's transaction distributions
     * @param ownerId - The OwnerId of the requested transaction
     * @param sourceId - The ID of the source that the transaction is a member of
     * @returns The transaction for the the passed in source ID
     */
    getTransactionFromSourceId(ownerId, sourceId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/sourceId/${sourceId}/transaction`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.get(url).pipe(map((response) => response));
    }
    /**
     * Import the transaction for matching owner
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param transactionDto - TransactionDto containing the transaction and transaction distributions
     * @returns The accepted status of the import
     */
    importTransaction(ownerId, transactionDto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/transaction/import`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.post(url, transactionDto).pipe(map((response) => response));
    }
    /**
     * Delete transactions for matching owner from matching journal
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param journalId - The ID of the Journal that the transaction is member of
     * @param transactionDto - TransactionDto containing the list of transaction and transaction distributions
     * @returns Deleted transactions
     */
    deleteTransactions(ownerId, journalId, transactionDto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/journal/${journalId}/transaction`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.post(url, { transactions: transactionDto }).pipe(map((response) => response.data));
    }
    /**
     * Updates list of the transactions
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param journalId - The ID of the Journal that the transaction is member of
     * @param transactionDto - TransactionDto containing the list of transaction and transaction distributions
     * @returns List of updated transactions
     */
    updateTransactions(ownerId, journalId, transactionDto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/journal/${journalId}/transaction`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.put(url, { transactions: transactionDto }).pipe(map((response) => response));
    }
    /**
     * Reverses the transaction for the next period
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param periodId - The ID of the next period
     * @returns List of reversed transactions
     */
    reverseTransaction(ownerId, periodId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/reverseTransaction/${periodId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.httpClient.post(url, null).pipe(map((response) => response.data));
    }
    /**
     * Import the transaction with import document link for matching owner
     * @param ownerId - The ID of the owner that this transaction belongs to
     * @param transactionWithDocumentLinkV1Dto - TransactionWithDocumentLinkV1Dto containing transaction and all transaction distributions
     * being added with import document link
     * @returns The accepted status of the import
     */
    importTransactionWithDocument(ownerId, transactionWithDocumentLinkV1Dto) {
        const url = UrlBase.getBaseUrl(ApiVersion.v2) + `${ownerId}/transaction/importWithDocumentLink`;
        return (this.httpClient
            .post(url, transactionWithDocumentLinkV1Dto)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((response) => response)));
    }
}
TransactionService.ɵfac = function TransactionService_Factory(t) { return new (t || TransactionService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TransactionService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TransactionService_Factory() { return new TransactionService(i0.ɵɵinject(i1.HttpClient)); }, token: TransactionService, providedIn: "root" });
TransactionService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TransactionService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

/**
 * DTO contains transactionV2Dto object with import document link
 */
class TransactionWithDocumentLinkV1Dto {
}

/**
 * DTO contains the identifier of the document storage folder id and unique S3ObjectReference
 */
class ImportDocumentLinkV1Dto {
}

class PreferencesServiceV2 {
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets application preferences V2 This will only work with V2 for the application and cateogry
     * @param preferenceParameters - for calling the endpoints
     * @returns application preferences
     */
    getApplicationPreferencesV2(preferenceParameters) {
        const url = UrlBase.getPreferencesUrl(ApiVersion.v2) +
            `/applications/${preferenceParameters.application}/categories/${preferenceParameters.category}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
    /**
     *
     * Gets contact preference
     * @param preferenceParameters - for calling the endpoints
     * @returns preference object for the current contact
     */
    getContactPreferenceV2(preferenceParameters) {
        const url = UrlBase.getPreferencesUrl(ApiVersion.v2) +
            `/applications/${preferenceParameters.application}` +
            `/categories/${preferenceParameters.category}/contacts/${preferenceParameters.contactId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
    /**
     * Updates preferences not sure if we can use this
     * @param preferences - preference object passed for updating that object
     * @returns preferences
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updatePreferencesV2(preferenceParameters) {
        const url = UrlBase.getPreferencesUrl(ApiVersion.v2) +
            `/applications/${preferenceParameters.application}/categories/${preferenceParameters.category}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, preferenceParameters.preference).pipe(map((response) => response));
    }
    /**
     *
     * Params Update preferences service
     * @param preferenceParameters - parameters for the endpoint
     * @returns contacts preferences updated
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateContactsPreferencesV2(preferenceParameters) {
        const url = UrlBase.getPreferencesUrl(ApiVersion.v2) +
            `/applications/${preferenceParameters.application}` +
            `/categories/${preferenceParameters.category}/contacts/${preferenceParameters.contactId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, preferenceParameters.preference).pipe(map((response) => response));
    }
}
PreferencesServiceV2.ɵfac = function PreferencesServiceV2_Factory(t) { return new (t || PreferencesServiceV2)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
PreferencesServiceV2.ɵprov = i0.ɵɵdefineInjectable({ factory: function PreferencesServiceV2_Factory() { return new PreferencesServiceV2(i0.ɵɵinject(i1.HttpClient)); }, token: PreferencesServiceV2, providedIn: "root" });
PreferencesServiceV2.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(PreferencesServiceV2, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

// this class can be extended by the consumer of the prefrences. they can add their own properties
class Preferences {
}
/**
 * preference object to save the preferences for application and category for V2
 */
class PreferenceV2 extends DtoBase {
}
/**
 * preference object for V1 to save the preferences for application and category
 */
class PreferencesV1 extends DtoBase {
}

var PreferencesCategoryType;
(function (PreferencesCategoryType) {
    PreferencesCategoryType["QBOImports"] = "quickbooks-import-preferences";
    PreferencesCategoryType["SSImports"] = "spreadsheet-import-preferences";
    PreferencesCategoryType["AccountGroupings"] = "accounts-groupings-preferences";
    PreferencesCategoryType["TrialBalance"] = "trial-balance-preferences";
    PreferencesCategoryType["Adjustments"] = "adjustment-preferences";
    PreferencesCategoryType["OnBalanceTransactions"] = "onbalance-transactions-preferences";
})(PreferencesCategoryType || (PreferencesCategoryType = {}));

class PreferenceParameters {
}

class FixedProfitShareTypeService {
    /**
     * constructor for Fixed Profit Share Types
     * @param httpClient - object of HttpClient
     */
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets fixed profit share types for matching fiscalYearId. Returns fixed profit share types
     * @param ownerId - The parent owner ID
     * @param fiscalYearId - The fiscal year ID that the Fixed Profit Share Type is created for
     * @returns The fixed profit share types for corresponding ownerID and fiscal year id
     */
    getFixedProfitShareTypes(ownerId, fiscalYearId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/fixedProfitShareType/fiscalYear/${fiscalYearId}`;
        return this.http.get(url).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => response.data), catchError(err => throwError(err)));
    }
    /**
     * Add or Update the fixed profit share types for matching fiscalYearId. Returns new fixed profit share types
     * @param ownerId - The parent owner ID
     * @param fiscalYearId - The fiscal year ID that the Fixed Profit Share Type is created for
     * @param fixedProfitShareTypes - The new fixed profit share type data
     * @returns The updated fixed profit share types for corresponding ownerId and fiscal year id
     */
    addOrUpdateFixedProfitShareTypes(ownerId, fiscalYearId, fixedProfitShareTypes) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/fixedProfitShareType/fiscalYear/${fiscalYearId}`;
        return this.http.put(url, fixedProfitShareTypes).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((response) => response.data), catchError(err => throwError(err)));
    }
}
FixedProfitShareTypeService.ɵfac = function FixedProfitShareTypeService_Factory(t) { return new (t || FixedProfitShareTypeService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
FixedProfitShareTypeService.ɵprov = i0.ɵɵdefineInjectable({ factory: function FixedProfitShareTypeService_Factory() { return new FixedProfitShareTypeService(i0.ɵɵinject(i1.HttpClient)); }, token: FixedProfitShareTypeService, providedIn: "root" });
FixedProfitShareTypeService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FixedProfitShareTypeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class FixedProfitShareType extends DtoBase {
}

class ChartOfAccountDescription {
}

class ChartOfAccountPrediction {
}

class ChartOfAccounts {
}

class TaxCodePrediction {
}

class QboAccountData {
}

var AccountingMethod;
(function (AccountingMethod) {
    AccountingMethod["Cash"] = "Cash";
    AccountingMethod["Accrual"] = "Accrual";
})(AccountingMethod || (AccountingMethod = {}));

var IncludeExcludeZeroBalance;
(function (IncludeExcludeZeroBalance) {
    IncludeExcludeZeroBalance["IncludeZeroBalance"] = "IncludeZeroBalance";
    IncludeExcludeZeroBalance["ExcludeZeroBalance"] = "ExcludeZeroBalance";
})(IncludeExcludeZeroBalance || (IncludeExcludeZeroBalance = {}));

class QuickbooksReportDataParams extends DtoBase {
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class QuickbooksReportDataService {
    constructor(http) {
        this.http = http;
    }
    /**
     * This endpoint returns the sorted order of account details based on account type then account number and account description,
     * and also account details contains the account balances from QBO service for the company linked to applicationQboEntityId with
     * the options passed in with the params object
     * @param reportDataParams - params object required to get the account details from QBo services
     */
    getQuickbooksReportData(reportDataParams) {
        let url = `${UrlBase.getBaseUrl(ApiVersion.v3)}qbo-accountdata-reports`;
        url += `?startDate=${reportDataParams.startDate}&`;
        url += `endDate=${reportDataParams.endDate}&`;
        url += `accountingMethod=${reportDataParams.accountingMethod}&`;
        url += `includeExcludeZeroBalance=${reportDataParams.includeExcludeZeroBalance}&`;
        url += `accountActivitiesOnly=${reportDataParams.accountActivitiesOnly.toString()}&`;
        url += `supportedApplicationId=${reportDataParams.supportedApplicationId}&`;
        url += `applicationQboEntityId=${reportDataParams.applicationQboEntityId}`;
        return this.http.get(url).pipe(map((response) => response));
    }
}
QuickbooksReportDataService.ɵfac = function QuickbooksReportDataService_Factory(t) { return new (t || QuickbooksReportDataService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
QuickbooksReportDataService.ɵprov = i0.ɵɵdefineInjectable({ factory: function QuickbooksReportDataService_Factory() { return new QuickbooksReportDataService(i0.ɵɵinject(i1.HttpClient)); }, token: QuickbooksReportDataService, providedIn: "root" });
QuickbooksReportDataService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(QuickbooksReportDataService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TransTypeService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets the trans type for given owner and description
     * @param ownerId - The owner id to get the trans type for.
     * @param description - The description of tran type to get
     * @returns Observable of transType
     */
    getSingleTransType(ownerId, description) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/transType?description=${description}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     * Gets all trans types for given owner
     * @param ownerId - The owner id to get the trans type for.
     * @returns Observable of transType array
     */
    getAllTransTypes(ownerId) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/transType`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
TransTypeService.ɵfac = function TransTypeService_Factory(t) { return new (t || TransTypeService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
TransTypeService.ɵprov = i0.ɵɵdefineInjectable({ factory: function TransTypeService_Factory() { return new TransTypeService(i0.ɵɵinject(i1.HttpClient)); }, token: TransTypeService, providedIn: "root" });
TransTypeService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(TransTypeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class TransType {
}

var TransTypeSystemIdEnum;
(function (TransTypeSystemIdEnum) {
    TransTypeSystemIdEnum["Adjusting"] = "ADJUSTING";
    TransTypeSystemIdEnum["Regular"] = "REGULAR";
    TransTypeSystemIdEnum["BeginningBalance"] = "BEGINNINGBALANCE";
    TransTypeSystemIdEnum["Potential"] = "POTENTIAL";
    TransTypeSystemIdEnum["Other"] = "OTHER";
    TransTypeSystemIdEnum["Reclassifying"] = "RECLASSIFYING";
    TransTypeSystemIdEnum["Tax"] = "TAX";
    TransTypeSystemIdEnum["ComparativeAdjustment"] = "COMPARATIVEADJUSTMENT";
    TransTypeSystemIdEnum["OpeningBalance"] = "OPENINGBALANCE";
    TransTypeSystemIdEnum["PriorPeriodAdjustment"] = "PRIORPERIODADJUSTMENT";
    TransTypeSystemIdEnum["Closing"] = "CLOSING";
    TransTypeSystemIdEnum["ProfileAllocation"] = "PROFITALLOCATION";
})(TransTypeSystemIdEnum || (TransTypeSystemIdEnum = {}));

class JournalService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets the journal for given owner and description
     * @param ownerId - The owner id to get the journal for.
     * @param description - The description of journal to get
     * @returns Observable of Journal
     */
    getJournal(ownerId, description) {
        const url = UrlBase.getBaseUrl(ApiVersion.v1) + `${ownerId}/journal/${description}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
JournalService.ɵfac = function JournalService_Factory(t) { return new (t || JournalService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
JournalService.ɵprov = i0.ɵɵdefineInjectable({ factory: function JournalService_Factory() { return new JournalService(i0.ɵɵinject(i1.HttpClient)); }, token: JournalService, providedIn: "root" });
JournalService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(JournalService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class Journal {
}

class AccountOpenBalanceAssignmentService {
    constructor(http) {
        this.http = http;
    }
    /**
     * Gets a list of account open balance assignments by given fiscal year ID
     * @param ownerId - The owner id
     * @param fiscalYearId - The fiscal year ID
     * @returns Observable of account open balance assignments
     */
    getByFiscalYear(ownerId, fiscalYearId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/accountOpenBalance/fiscalYear/${fiscalYearId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response.data));
    }
    /**
     *
     * @param ownerId - The owner id
     * @param fiscalYearId - The fiscal year ID
     * @param accountOpeningBalanceAssignment - The account opening balance assignment data
     * @returns Observable of account open balance assignments
     */
    updateAccountOpenBalance(ownerId, fiscalYearId, accountOpeningBalanceAssignment) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/accountOpenBalance/fiscalYear/${fiscalYearId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.put(url, accountOpeningBalanceAssignment).pipe(map((response) => response.data));
    }
}
AccountOpenBalanceAssignmentService.ɵfac = function AccountOpenBalanceAssignmentService_Factory(t) { return new (t || AccountOpenBalanceAssignmentService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
AccountOpenBalanceAssignmentService.ɵprov = i0.ɵɵdefineInjectable({ factory: function AccountOpenBalanceAssignmentService_Factory() { return new AccountOpenBalanceAssignmentService(i0.ɵɵinject(i1.HttpClient)); }, token: AccountOpenBalanceAssignmentService, providedIn: "root" });
AccountOpenBalanceAssignmentService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AccountOpenBalanceAssignmentService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class AccountOpenBalanceAssignment extends DtoBase {
}

class WorkpaperReferenceMetadata {
}

class ProjectService {
    constructor(http) {
        this.http = http;
    }
    /**
     * gets a project for given companyId and scheduleItemId
     * @param companyId - id of company associated with project
     * @param scheduleItemId -  id of schedule item associated with project
     * @returns observable with type project associated with companyId and scheduleItemId
     */
    getProject(companyId, scheduleItemId) {
        const url = UrlBase.getCoreBaseUrl(ApiVersion.v8) + `companies/${companyId}/projects/${scheduleItemId}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get(url).pipe(map((response) => response));
    }
    /**
     * Returns projects list matching specific criterias
     * @param companyId - id of the company
     * @param projectsRequestParams - request params object
     * @returns projects
     */
    getProjects(companyId, projectsRequestParams) {
        const url = `${UrlBase.getCoreBaseUrl(ApiVersion.v3)}companies/${companyId}/schedule-item-views/search`;
        const body = projectsRequestParams;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.post(url, body).pipe(map((response) => response.items));
    }
}
ProjectService.ɵfac = function ProjectService_Factory(t) { return new (t || ProjectService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
ProjectService.ɵprov = i0.ɵɵdefineInjectable({ factory: function ProjectService_Factory() { return new ProjectService(i0.ɵɵinject(i1.HttpClient)); }, token: ProjectService, providedIn: "root" });
ProjectService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ProjectService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

/**
 * a model for the project
 */
class Project {
}

/**
 * a model for the For contact property in project
 */
class ProjectForContact {
}

class FilterSearchAndSortRequest {
}

class PagingDataRequest {
}

class ProjectsRequestParams {
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class RoundingSettingService {
    constructor(http) {
        this.http = http;
    }
    // get Rounding Settings for the given owner id
    getRoundingSettings(ownerId) {
        const url = `${UrlBase.getBaseUrl(ApiVersion.v1)}${ownerId}/roundingSetting`;
        return this.http.get(url).pipe(map((response) => response.data));
    }
}
RoundingSettingService.ɵfac = function RoundingSettingService_Factory(t) { return new (t || RoundingSettingService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient)); };
RoundingSettingService.ɵprov = i0.ɵɵdefineInjectable({ factory: function RoundingSettingService_Factory() { return new RoundingSettingService(i0.ɵɵinject(i1.HttpClient)); }, token: RoundingSettingService, providedIn: "root" });
RoundingSettingService.ctorParameters = () => [
    { type: HttpClient }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(RoundingSettingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc1.HttpClient }]; }, null); })();

class RoundingAccountV1Dto {
}

class RoundingSettingV1Dto {
}

/*
 * Public API Surface of accounting-services
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Account, AccountBalance, AccountBalanceService, AccountBalanceTotalService, AccountBalanceType, AccountBalanceTypeDescriptions, AccountBalanceTypeService, AccountBalanceTypeSystemId, AccountDetails, AccountDetailsV2Dto, AccountGroup, AccountGroupService, AccountMapping, AccountMappingService, AccountOpenBalanceAssignment, AccountOpenBalanceAssignmentService, AccountService, AccountTag, AccountTagService, AccountTagV2Dto, AccountToExternalAccountMappingV2Dto, AccountTotals, AccountTransactionSourceType, AccountWithAccountGroupLinks, AccountWithLinks, AccountWithTaxScheduleLinks, AccountingMethod, AccountingServicesModule, AmountFormattingService, ApplicationType, ApplicationTypeId, AssignedCategoryNodeAccount, AssignedTaxCodeAccount, CategoryNode, CategoryNodeAccount, CategoryNodeAccountList, CategoryNodeAccountService, CategoryNodeOption, CategoryNodeService, ChartOfAccountDescription, ChartOfAccountPrediction, ChartOfAccounts, CompanyClient, CompanyInfo, CompanyOwnerContact, CompanyService, Contact, ContactRelationshipExpansionV4Dto, ContactsService, Country, CountryCodes, CurrencyTypeEnum, DocumentUploadService, ExternalAccountMapping, ExternalAccountMappingService, ExternalAccountMappingType, ExternalAccountMappingV2Dto, FilterSearchAndSortRequest, FinancialReportCategory, FinancialReportCategoryId, FinancialReportCategoryService, FinancialReportDetail, FinancialReportTypes, FiscalYear, FiscalYearService, FixedProfitShare, FixedProfitShareType, FixedProfitShareTypeService, FrequencyType, ImportAccountService, ImportAccountsWithBalances, ImportAccountsWithDocumentLinkV1Dto, ImportAccountsWithOutOfBalanceAccount, ImportAccountsWithOutOfBalanceWithDocumentLinkV1Dto, ImportDocumentLinkV1Dto, ImportOutOfBalancesAccounts, ImportV3Dto, IncludeExcludeZeroBalance, IncludeType, IntuitQboDefaultAccountGroupingMappingService, IntuitQboDefaultTaxGroupingMappingService, Journal, JournalService, NamedReferenceDto, OnvioAccountDetailsForQboMapping, OnvioAccountMappingToQboAccount, OpenBalance, PagingDataRequest, Period, PeriodService, PeriodType, PreferenceParameters, PreferenceV2, Preferences, PreferencesCategoryType, PreferencesServiceV1, PreferencesServiceV2, PreferencesV1, Product, ProfitAllocation, ProfitAllocationDetails, ProfitAllocationDetailsService, Project, ProjectForContact, ProjectService, ProjectsRequestParams, QboAccountData, QboAccountDetailsForQboMapping, QboExternalAccountMapping, QuickbooksReportDataParams, QuickbooksReportDataService, RoundingAccountV1Dto, RoundingSettingService, RoundingSettingV1Dto, SortingService, TaxCode, TaxCodeAccount, TaxCodeAccountService, TaxCodeOption, TaxCodePrediction, TaxCodeService, TaxExportOption, TaxSchedule, TaxScheduleService, TaxVendor, TaxVendorService, Tickmark, TickmarkService, TotalBalanceCondition, TransDistV1Dto, TransType, TransTypeService, TransTypeSystemIdEnum, TransactionService, TransactionSubTypeV1Dto, TransactionV1Dto, TransactionV2Dto, TransactionWithDocumentLinkV1Dto, TransactionWorkpaperReferenceV1Dto, TrialBalanceAccount, TrialBalanceColumn, TrialBalanceColumnAmount, TrialBalanceColumnValue, TrialBalanceGrid, TrialBalanceGridData, TrialBalanceGridDataParams, TrialBalanceGridSourceType, TrialBalanceRow, TrialBalanceService, UpdateTrialBalanceColumnValueDto, ValueType, WorkpaperReference, WorkpaperReferenceMetadata, YearEndCloseService, DtoBase as ɵa, DtoBaseCapitalCase as ɵb };

